/**
 * Backtest runner and results.
 *
 * The engine's assumption list is rendered with the results rather than hidden
 * behind a tooltip: a Sharpe ratio means nothing without knowing when orders
 * were assumed to fill and what costs were charged.
 */
import { formatNum, formatPct } from "@tt/shared";
import { AnimatePresence, motion } from "motion/react";
import { useEffect, useRef, useState } from "react";

import { useAction, usePersisted } from "../hooks/useApi";
import { ApiError, api } from "../lib/api";
import { useTradeFocuser } from "./tradeNav";

interface BacktestPayload {
  symbol: string;
  declaration?: { title?: string; kind?: string };
  metrics: Record<string, number | string | null | number[]>;
  equity: (number | null)[];
  timestamps: string[];
  trades: {
    direction: string; entry_ts: string | null; exit_ts: string | null;
    entry_price: number; exit_price: number | null; pnl: number; pnl_pct: number;
    /** Number of shares/contracts, as the broker sized the trade. Used by
     *  the TV-style trade list's Size column. */
    qty: number;
    exit_reason: string | null; bars_held: number;
    /** Max favorable excursion as a fraction of entry price (how far the
     *  trade went in your favour before closing). TradingView calls these
     *  "Favorable excursion" and "Adverse excursion" in the per-trade panel. */
    mfe?: number;
    mae?: number;
  }[];
  warnings: string[];
  script_warnings: string[];
  assumptions: string[];
}

/** The engine's limits. A `max` attribute stops the spinner arrows but not
 *  typing, and it does nothing at all to a value restored from storage -- a
 *  15000 saved before this cap existed came back every session and failed
 *  every run. Clamped on entry and again on submit. */
const BARS_MIN = 100;
const BARS_MAX = 50000;

function clampBars(n: number): number {
  if (!Number.isFinite(n)) return 1500;
  return Math.min(BARS_MAX, Math.max(BARS_MIN, Math.round(n)));
}

// Max bars the engine accepts for a backtest. Raised from 5 000 to 50 000
// so a 1-minute strategy can span months rather than just a few trading
// hours. The engine's own bar store (bars_1m) retains years of history.


/** Approximate bars per calendar period, by chart timeframe. Multipliers are
 *  rounded for US cash equities (6.5 trading hours per session, ~252 sessions
 *  per year) because the clamp below caps everything at 5000 anyway, and a
 *  slightly-off bar count is better than making the user think about this. */
function barsForRange(range: "7D" | "1M" | "3M" | "6M" | "1Y" | "All", tf: string): number {
  if (range === "All") return 50000;
  if (range === "7D") return Math.max(100, Math.round(barsForRange("1M", tf) / 4));
  const monthsByRange = { "1M": 1, "3M": 3, "6M": 6, "1Y": 12 }[range as "1M" | "3M" | "6M" | "1Y"];
  const barsPerMonth: Record<string, number> = {
    "1m": 390 * 21,
    "5m": 78 * 21,
    "15m": 26 * 21,
    "30m": 13 * 21,
    "1h": 7 * 21,
    "4h": 2 * 21,
    "1d": 21,
    "1w": 4,
    "1M": 1,
  };
  const base = barsPerMonth[tf] ?? 21;
  // Clamp into the same window as clampBars() -- bumped to 50 000 so
  // Available chart range / Entire history on 1-minute bars can fetch
  // more than two weeks of history.
  return Math.min(50000, Math.max(100, Math.round(base * monthsByRange)));
}

export function BacktestPanel({
  source, symbol, timeframe,
}: {
  source: string; symbol: string; timeframe: string;
}) {
  const [bars, setBars] = usePersisted("tt.bt.bars", 1500);
  // Which range chip was last clicked. Tracked explicitly because several
  // chips can resolve to the same clamped bar count on fast timeframes
  // (6M, 1Y, All all cap at 5000 on 1m), and comparing `bars` alone would
  // light up all three.
  const [activeRange, setActiveRange] = usePersisted<"7D" | "1M" | "3M" | "6M" | "1Y" | "All" | "ALL_HISTORY" | "">(
    "tt.bt.activeRange", "",
  );
  // Match TradingView's default initial capital. Was 100K; users who want
  // a different value override via the 💵 dropdown or Settings dialog.
  const [capital, setCapital] = usePersisted("tt.bt.capital", 1_000);
  // Chart-style toggle in the Strategy Tester's left-side icon strip,
  // persisted so a reload keeps the last choice.
  const [curveStyleShared, setCurveStyleShared] = usePersisted<"area" | "bars">(
    "tt.bt.curve", "area",
  );
  const [qtyValue, setQtyValue] = usePersisted("tt.bt.qty", 95);
  const [slippage, setSlippage] = usePersisted("tt.bt.slip", 2);
  const [commission, setCommission] = usePersisted("tt.bt.comm", 0.01);
  const [allowShort, setAllowShort] = usePersisted("tt.bt.short", true);
  const [pyramiding, setPyramiding] = usePersisted("tt.bt.pyramiding", 0);
  // TradingView-style broker-emulator fields. 0 for leverage = "no cap"
  // (TV calls it Infinity); the engine treats it the same way.
  const [longLev, setLongLev] = usePersisted("tt.bt.longlev", 0);
  const [shortLev, setShortLev] = usePersisted("tt.bt.shortlev", 0);
  const [orderDelay, setOrderDelay] = usePersisted("tt.bt.orderdelay", 0);
  const [limitExec, setLimitExec] = usePersisted<"requested_price" | "allow_slippage">(
    "tt.bt.limitexec", "requested_price",
  );
  // Optional custom date range. Overrides the chip-based bar count when set.
  const [startDate, setStartDate] = usePersisted("tt.bt.start", "");
  const [endDate, setEndDate] = usePersisted("tt.bt.end", "");
  // Bar detalization matches TradingView's dropdown. "Default" fills orders
  // at the next bar's open (the engine's default); "High" fills at the
  // signal bar's own close (process_orders_on_close), which is less
  // realistic but matches a strategy that reacts intrabar.
  const [detalization, setDetalization] = usePersisted<"default" | "high">(
    "tt.bt.detalization", "default",
  );
  const onClose = detalization === "high";
  const [settingsOpen, setSettingsOpen] = useState(false);

  const bt = useAction(() =>
    api.backtest({
      source, symbol, timeframe, bars: clampBars(bars),
      initial_capital: capital,
      qty_type: "percent_of_equity",
      qty_value: qtyValue,
      commission_type: "percent",
      commission_value: commission,
      slippage_bps: slippage,
      allow_short: allowShort,
      pyramiding,
      long_leverage: longLev,
      short_leverage: shortLev,
      order_execution_delay_bars: orderDelay,
      limit_order_execution: limitExec,
      start_date: startDate || null,
      end_date: endDate || null,
      process_orders_on_close: onClose,
    }) as Promise<BacktestPayload>,
  );

  const r = bt.data;
  const m = r?.metrics ?? {};
  const pineErr = bt.error instanceof ApiError ? bt.error.pineError : null;

  // Broadcast trades so the price chart can render entry/exit arrows for
  // the current strategy. One BroadcastChannel per app so pop-out windows
  // pick it up too. Only publishes when the Style tab's "Trades on chart"
  // toggle is on.
  const tradesOnChart = (() => {
    try { return localStorage.getItem("tt.bt.style.trades") !== "false"; }
    catch { return true; }
  })();
  useEffect(() => {
    if (!r || !tradesOnChart) {
      // Clearing the channel so the chart drops any previously drawn markers
      // when the user turns the toggle off or the dock closes.
      try {
        const ch = new BroadcastChannel("tt-backtest-trades");
        ch.postMessage({ symbol, timeframe, trades: [] });
        ch.close();
      } catch { /* no BroadcastChannel in this browser */ }
      return;
    }
    try {
      const ch = new BroadcastChannel("tt-backtest-trades");
      ch.postMessage({
        symbol, timeframe,
        trades: r.trades.map((t, i) => ({
          number: i + 1,
          direction: t.direction,
          entry_ts: t.entry_ts,
          exit_ts: t.exit_ts,
          entry_price: t.entry_price,
          exit_price: t.exit_price,
          pnl: t.pnl,
        })),
      });
      ch.close();
    } catch { /* same */ }
  }, [r, symbol, timeframe, tradesOnChart]);

  // When the chart's timeframe changes, recompute the bar count for the
  // active range chip. Without this, "6M" selected on 1m locks the backtest
  // to ~5000 bars (= less than a day), and switching the chart to 1d does
  // not widen the backtest window. Only runs when a chip is active -- a
  // user who typed a custom bar count is left alone.
  useEffect(() => {
    if (!activeRange) return;
    const target = activeRange === "ALL_HISTORY"
      ? 50000
      : barsForRange(activeRange, timeframe);
    if (target !== bars) setBars(target);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeframe, activeRange]);

  // Auto-run on mount so switching to this tab from the Pine editor (where
  // the user already saw their script compile) shows the Strategy Tester
  // immediately -- the TradingView moment where "Strategy Tester" appears
  // below the chart after you add a strategy. One-shot, keyed on
  // (symbol, timeframe, source) so it doesn't re-fire while the user edits
  // the control panel; manual Run still works.
  const autoRanFor = useRef("");
  useEffect(() => {
    if (!source.trim() || !symbol) return;
    // Include bars + date range + active chip in the key so clicking "7D"
    // or picking a custom Testing period actually re-runs the backtest.
    // Previously only (symbol, timeframe, source.length) was tracked, so
    // chip clicks changed `bars` but no fresh request fired.
    // Every input the engine sees goes into the key so changing capital,
    // detalization, or any Settings field actually re-runs the backtest
    // instead of silently showing the previous result.
    const key = [
      symbol, timeframe, source.length, bars, startDate, endDate, activeRange,
      capital, qtyValue, slippage, commission, allowShort, pyramiding,
      longLev, shortLev, orderDelay, limitExec, onClose,
    ].join("|");
    if (autoRanFor.current === key) return;
    autoRanFor.current = key;
    void bt.run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    source, symbol, timeframe, bars, startDate, endDate, activeRange,
    capital, qtyValue, slippage, commission, allowShort, pyramiding,
    longLev, shortLev, orderDelay, limitExec, onClose,
  ]);

  const num = (k: string) => {
    const v = m[k];
    return typeof v === "number" ? v : null;
  };

  return (
    <div className="panel">
      {/* Single toolbar row across the top of the Strategy Tester, matching
          TradingView's dock header: run button, date-range chips, capital
          and the rest of the controls, all inline. */}
      {/* TradingView-style dock toolbar: run button, date range chips,
          window summary, capital preset, bar detalization, and the usual
          backtest controls -- all in one horizontal row. */}
      <div className="st-toolbar">
        {bt.loading && (
          <span className="st-busy" title="Backtest running">
            <span className="spinner" />
            <ElapsedSeconds />
          </span>
        )}

        {/* TradingView-style chart-style icons on the far left: line and
            bars. Toggles the equity-curve rendering style persistently. */}
        <div className="st-chart-style">
          {(["area", "bars"] as const).map((style) => (
            <button
              key={style}
              className={`st-cs-icon ${(curveStyleShared === style) ? "st-cs-on" : ""}`}
              onClick={() => setCurveStyleShared(style)}
              title={style === "area" ? "Line/area chart" : "Bars chart"}
            >
              {style === "area" ? (
                <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor"
                     strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="3 17 9 11 13 15 21 7" />
                </svg>
              ) : (
                <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor"
                     strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
                  <line x1="6" y1="4" x2="6" y2="20" />
                  <line x1="10" y1="8" x2="10" y2="16" />
                  <line x1="14" y1="2" x2="14" y2="22" />
                  <line x1="18" y1="6" x2="18" y2="18" />
                </svg>
              )}
            </button>
          ))}
        </div>
        <span className="st-tool-sep" />

        {/* TradingView-style pill dropdowns: date range, capital, bar
            detalization. Each is a button that shows a popover on click. */}
        <TvDropdown
          icon="calendar"
          label={(() => {
            // Named presets win over the raw date range so the user can see
            // which Testing-period mode they picked, not just the window it
            // happened to resolve to.
            const presetName = ({
              "ALL_HISTORY": "Entire history",
              "7D": "Last 7 days",
              "1M": "Last 30 days",
              "3M": "Last 90 days",
              "1Y": "Last 365 days",
              "All": "",   // "Available chart range" is the default — show the dates
              "6M": "",
              "": "",
            } as Record<string, string>)[activeRange] ?? "";
            if (presetName) return presetName;
            if (startDate && endDate) return `${startDate} — ${endDate}`;
            const first = r?.timestamps?.[0]?.slice(0, 10);
            const lastIdx = (r?.timestamps?.length ?? 0) - 1;
            const last = lastIdx >= 0 ? r?.timestamps?.[lastIdx]?.slice(0, 10) : undefined;
            return first && last ? `${first} — ${last}` : "Testing period";
          })()}
          title="Testing period"
        >
          {(close) => (
            <>
              <div className="tvdd-head">
                <span>Testing period</span>
                <button className="tvdd-reset" onClick={() => {
                  setBars(5000); setActiveRange("All");
                  setStartDate(""); setEndDate("");
                  close();
                }}>Reset</button>
              </div>
              {([
                ["All", "Available chart range", "Default"],
                ["7D", "Last 7 days", ""],
                ["1M", "Last 30 days", ""],
                ["3M", "Last 90 days", ""],
                ["1Y", "Last 365 days", ""],
                ["ALL_HISTORY", "Entire history", ""],
              ] as const).map(([k, label, hint]) => (
                <button
                  key={k}
                  className={`tvdd-item ${activeRange === k ? "tvdd-item-on" : ""}`}
                  onClick={() => {
                    // "Entire history" asks the engine for its maximum bar
                    // cap; the gateway caps at 50 000 which is 200+ years of
                    // daily bars or ~1 year of 1-minute bars.
                    const target = k === "ALL_HISTORY" ? 50000 : barsForRange(k, timeframe);
                    setBars(target);
                    setActiveRange(k);
                    setStartDate(""); setEndDate("");
                    close();
                  }}
                >
                  <span>{label}</span>
                  <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    {hint && <span className="tvdd-hint">{hint}</span>}
                    {activeRange === k && <span className="tvdd-check">✓</span>}
                  </span>
                </button>
              ))}
              <div className="tvdd-sep" />
              <div className="tvdd-custom">
                <div className="tvdd-sub">Custom date range</div>
                <div className="tvdd-pair">
                  <input type="date" value={startDate}
                         onChange={(e) => setStartDate(e.target.value)} />
                  <span>—</span>
                  <input type="date" value={endDate}
                         onChange={(e) => setEndDate(e.target.value)} />
                </div>
                <button
                  className="tvdd-apply"
                  disabled={!startDate && !endDate}
                  onClick={() => { setActiveRange(""); close(); void bt.run(); }}
                >Apply</button>
              </div>
            </>
          )}
        </TvDropdown>

        <TvDropdown
          icon="dollar"
          label={capital >= 1_000_000 ? `${(capital / 1_000_000).toFixed(0)}M USD`
                : capital >= 1000 ? `${(capital / 1000).toFixed(0)}K USD`
                : `${capital} USD`}
          title="Initial capital"
        >
          {(close) => (
            <>
              <div className="tvdd-head"><span>Initial capital</span></div>
              <div className="tvdd-cap">
                <input type="number" value={capital}
                       onChange={(e) => setCapital(Number(e.target.value))} />
                <select defaultValue="USD" disabled>
                  <option value="USD">USD</option>
                </select>
              </div>
              <div className="tvdd-presets">
                {[1000, 10000, 100000, 1000000].map((v) => (
                  <button key={v}
                          className={`tvdd-chip ${capital === v ? "tvdd-chip-on" : ""}`}
                          onClick={() => { setCapital(v); close(); }}
                  >${v >= 1_000_000 ? `${v / 1_000_000}M` : `${v / 1000}K`}</button>
                ))}
              </div>
            </>
          )}
        </TvDropdown>

        <TvDropdown
          icon="scope"
          label={detalization === "high" ? "High detalization" : "Default detalization"}
          title="Bar detalization"
        >
          {(close) => (
            <>
              <div className="tvdd-head"><span>Bar detalization</span></div>
              <button className={`tvdd-item ${detalization === "default" ? "tvdd-item-on" : ""}`}
                      onClick={() => { setDetalization("default"); close(); }}>
                <span>
                  <div><strong>Default detalization</strong></div>
                  <div className="tvdd-sub">4 ticks per bar</div>
                </span>
                {detalization === "default" && <span className="tvdd-check">✓</span>}
              </button>
              <button className={`tvdd-item ${detalization === "high" ? "tvdd-item-on" : ""}`}
                      onClick={() => { setDetalization("high"); close(); }}>
                <span>
                  <div><strong>High detalization</strong></div>
                  <div className="tvdd-sub">~24 ticks per bar</div>
                </span>
                {detalization === "high" && <span className="tvdd-check">✓</span>}
              </button>
            </>
          )}
        </TvDropdown>

        {/* Right-side icon cluster: re-run + gear, matching TV's right-of-
            detalization icons. */}
        <span className="st-tool-sep" />
        <button
          className="icon-btn small"
          title="Re-run the backtest"
          onClick={() => void bt.run()}
          disabled={bt.loading}
        >↻</button>

        {/* Legacy label kept for backward compat; hidden */}
        <label className="small" style={{ display: "none" }}>
          <select value={detalization}
            onChange={(e) => setDetalization(e.target.value as "default" | "high")}>
            <option value="default">Default</option><option value="high">High</option>
          </select>
        </label>

        <span className="st-tool-sep" />

        {/* All the detailed controls live in a modal, matching TV's gear
            icon → Strategy Settings dialog with Inputs / Properties / Style /
            Visibility tabs. Keeps the toolbar at the clean "run + range +
            capital + detalization + gear" shape TV uses. */}
        <button
          className="icon-btn small"
          title="Strategy settings"
          onClick={() => setSettingsOpen(true)}
        >⚙</button>
      </div>

      <AnimatePresence>
        {settingsOpen && (
          <StrategySettings
            open={settingsOpen}
            onClose={() => setSettingsOpen(false)}
            capital={capital} setCapital={setCapital}
            qtyValue={qtyValue} setQtyValue={setQtyValue}
            slippage={slippage} setSlippage={setSlippage}
            commission={commission} setCommission={setCommission}
            allowShort={allowShort} setAllowShort={setAllowShort}
            pyramiding={pyramiding} setPyramiding={setPyramiding}
            longLev={longLev} setLongLev={setLongLev}
            shortLev={shortLev} setShortLev={setShortLev}
            orderDelay={orderDelay} setOrderDelay={setOrderDelay}
            limitExec={limitExec} setLimitExec={setLimitExec}
            startDate={startDate} setStartDate={setStartDate}
            endDate={endDate} setEndDate={setEndDate}
            detalization={detalization} setDetalization={setDetalization}
            bars={bars} setBars={setBars}
            scriptTitle={String(r?.declaration?.title ?? "Strategy")}
          />
        )}
      </AnimatePresence>

      {pineErr && (
        <div className="err">
          {pineErr.kind}: {pineErr.message} (line {pineErr.line}:{pineErr.col})
          {pineErr.hint ? `\nhint: ${pineErr.hint}` : ""}
        </div>
      )}
      {bt.error && !pineErr && <div className="err">{bt.error.message}</div>}

      {!r && !bt.loading && !bt.error && (
        <div className="notebox">
          Write a script with <span className="mono">strategy(...)</span> and{" "}
          <span className="mono">strategy.entry</span> / <span className="mono">strategy.close</span>{" "}
          in the Pine tab, then run it here. Orders fill at the next bar's open by default,
          because filling at the signal bar's own close is not something you could have done.
        </div>
      )}

      {r && (
        <StrategyTester r={r} m={m} num={num}
                        curveStyle={curveStyleShared} setCurveStyle={setCurveStyleShared} />
      )}
    </div>
  );
}

/** TradingView-style three-tab strategy results.
 *
 *  Overview is the dashboard everyone looks at first: the equity curve plus
 *  the six headline numbers that decide whether the strategy is worth
 *  tweaking. Performance is the full metric suite for someone who already
 *  liked what they saw and is now stress-testing it. Trades is the raw log,
 *  for debugging a specific entry or exit that looked wrong on the chart.
 */
/** Tiny "0.4s" / "12s" / "1m 03s" counter rendered next to the busy spinner
 *  so a long-running backtest doesn't look stuck. Resets on each mount
 *  (the dock remounts it whenever bt.loading flips from false to true, since
 *  it's only rendered then). */
function ElapsedSeconds() {
  const [t, setT] = useState(0);
  useEffect(() => {
    const start = performance.now();
    const id = setInterval(() => setT((performance.now() - start) / 1000), 250);
    return () => clearInterval(id);
  }, []);
  const label = t < 1
    ? `${t.toFixed(1)}s`
    : t < 60
      ? `${t.toFixed(0)}s`
      : `${Math.floor(t / 60)}m ${String(Math.floor(t % 60)).padStart(2, "0")}s`;
  return <span className="st-elapsed">{label}</span>;
}

function StrategyTester({ r, m, num, curveStyle, setCurveStyle }: {
  r: BacktestPayload;
  m: BacktestPayload["metrics"];
  num: (k: string) => number | null;
  curveStyle: "area" | "bars";
  setCurveStyle: (v: "area" | "bars") => void;
}) {
  type Tab = "overview" | "performance" | "tradesan" | "trades";
  const [tab, setTab] = usePersisted<Tab>("tt.bt.tab", "overview");
  const ci = Array.isArray(m.win_rate_ci) ? (m.win_rate_ci as number[]) : null;

  return (
    <div className="st-root">
      {r.warnings.map((w, i) => <div className="warnbox" key={i}>{w}</div>)}
      {r.script_warnings.map((w, i) => <div className="warnbox" key={`s${i}`}>{w}</div>)}

      {/* Headline strip, always visible. TV-style dual-line stats so the
          dollar and percent sit together: a net profit of $299 means
          something very different on $1k capital vs $100k. */}
      <div className="st-headline">
        <HeadlineStat
          label="Net profit"
          value={num("net_profit") !== null ? `${num("net_profit")! >= 0 ? "+" : ""}${formatNum(num("net_profit"), 0)}` : "—"}
          sub={formatPct(num("total_return"))}
          tone={num("total_return")}
        />
        <HeadlineStat
          label="Max drawdown"
          value={num("max_drawdown") !== null && num("initial_capital") !== null
            ? formatNum(Math.abs((num("max_drawdown") ?? 0) * (num("initial_capital") ?? 0)), 0)
            : "—"}
          sub={formatPct(num("max_drawdown"))}
          tone={-1}
        />
        <HeadlineStat
          label="Profitable trades"
          value={num("win_rate") !== null ? formatPct(num("win_rate")) : "—"}
          sub={(() => {
            const t = num("trades") ?? 0;
            const w = Math.round((num("win_rate") ?? 0) * t);
            return t > 0 ? `${w}/${t}` : "—";
          })()}
        />
        <HeadlineStat label="Profit factor" value={formatNum(num("profit_factor"))} />
        <HeadlineStat label="Sharpe" value={formatNum(num("sharpe"))} />
        <HeadlineStat label="Total trades" value={String(num("trades") ?? "—")} />
      </div>

      <div className="st-tabs" role="tablist">
        {([
          ["overview", "Overview"],
          ["performance", "Performance analysis"],
          ["tradesan", "Trades analysis"],
          ["trades", `List of Trades · ${r.trades.length}`],
        ] as const).map(([key, label]) => (
          <button
            key={key}
            role="tab"
            aria-selected={tab === key}
            className={`st-tab ${tab === key ? "st-tab-on" : ""}`}
            onClick={() => setTab(key)}
          >{label}</button>
        ))}
      </div>

      {tab === "overview" && (
        <div className="st-pane">
          <EquityCurve equity={r.equity} timestamps={r.timestamps} trades={r.trades}
                       curveStyle={curveStyle} setCurveStyle={setCurveStyle} />
          {(m.long_side || m.short_side) && (
            <PnlBySide
              long={(m.long_side as unknown) as { trades: number; pnl: number | null; win_rate: number | null }}
              short={(m.short_side as unknown) as { trades: number; pnl: number | null; win_rate: number | null }}
            />
          )}
          {ci && (
            <div className="notebox">
              The win rate's 95% interval is{" "}
              <span className="mono">
                {formatPct(ci[0])} – {formatPct(ci[1])}
              </span>{" "}
              on {num("trades")} trades. With a wide interval, the point estimate is not
              a property of the strategy — it is a property of this sample.
              {num("sharpe_psr") !== null && (
                <> The probability the true Sharpe exceeds zero, given this sample's
                  length and shape, is{" "}
                  <span className="mono">{formatPct(num("sharpe_psr"), 0)}</span>.</>
              )}
            </div>
          )}
          <h3>Assumptions baked into these numbers</h3>
          <div className="notebox">
            <ul className="tight">
              {r.assumptions.map((a, i) => <li key={i}>{a}</li>)}
            </ul>
          </div>
        </div>
      )}

      {tab === "performance" && (
        <div className="st-pane">
          <PerformanceSummary num={num} m={m} />
        </div>
      )}

      {tab === "tradesan" && (
        <div className="st-pane">
          <TradesAnalysis m={m} trades={r.trades} />
        </div>
      )}

      {tab === "trades" && (
        <TradesTable trades={r.trades} />
      )}
    </div>
  );
}

function HeadlineStat({ label, value, sub, tone }: {
  label: string; value: string; sub?: string; tone?: number | null;
}) {
  const color = tone === undefined || tone === null ? undefined
    : tone > 0 ? "var(--up)" : tone < 0 ? "var(--down)" : undefined;
  return (
    <div className="st-hs">
      <div className="st-hs-k">{label}</div>
      <div className="st-hs-v" style={{ color }}>{value}</div>
      {sub && <div className="st-hs-sub" style={{ color }}>{sub}</div>}
    </div>
  );
}

/** The full metric suite, grouped. Mirrors what TradingView's Strategy Tester
 *  Performance Summary tab shows, with explicit units where TV omits them. */
function PerformanceSummary({ num, m }: {
  num: (k: string) => number | null;
  m: BacktestPayload["metrics"];
}) {
  type Sub = "breakdown" | "periodical" | "benchmarking" | "growth";
  const [sub, setSub] = usePersisted<Sub>("tt.bt.perfsub", "breakdown");

  return (
    <>
      <div className="st-subtabs" role="tablist">
        {([
          ["breakdown", "Breakdown"],
          ["periodical", "Periodical"],
          ["benchmarking", "Benchmarking"],
          ["growth", "Growth and decline"],
        ] as const).map(([key, label]) => (
          <button
            key={key}
            className={`st-subtab ${sub === key ? "st-subtab-on" : ""}`}
            onClick={() => setSub(key)}
          >{label}</button>
        ))}
      </div>

      {sub === "breakdown" && <BreakdownView num={num} m={m} />}
      {sub === "periodical" && <PeriodicalView m={m} />}
      {sub === "benchmarking" && <BenchmarkingView m={m} />}
      {sub === "growth" && <GrowthDeclineView m={m} />}
    </>
  );
}

function BreakdownView({ num, m }: {
  num: (k: string) => number | null;
  m: BacktestPayload["metrics"];
}) {
  const ci = Array.isArray(m.win_rate_ci) ? (m.win_rate_ci as number[]) : null;
  return (
    <div className="st-perf">
      <PerfGroup title="Returns">
        <PerfRow k="Total return" v={formatPct(num("total_return"))}
                 tone={num("total_return")} />
        <PerfRow k="CAGR" v={formatPct(num("cagr"))} tone={num("cagr")} />
        <PerfRow k="Avg trade" v={formatPct(num("avg_trade_pct"), 2)}
                 tone={num("avg_trade_pct")} />
        <PerfRow k="Exposure" v={formatPct(num("exposure"), 0)}
                 bar={num("exposure")} />
      </PerfGroup>
      <PerfGroup title="Risk-adjusted">
        <PerfRow k="Sharpe" v={formatNum(num("sharpe"))} tone={num("sharpe")} />
        <PerfRow k="Sortino" v={formatNum(num("sortino"))} tone={num("sortino")} />
        <PerfRow k="Calmar" v={formatNum(num("calmar"))} tone={num("calmar")} />
        <PerfRow k="Deflated Sharpe" v={num("sharpe_psr") !== null ? formatPct(num("sharpe_psr"), 0) : "—"}
                 note="Probability true Sharpe > 0 given sample size and shape."
                 bar={num("sharpe_psr")} />
      </PerfGroup>
      <PerfGroup title="Drawdown">
        <PerfRow k="Max drawdown" v={formatPct(num("max_drawdown"))}
                 tone={num("max_drawdown")} bar={num("max_drawdown")} />
      </PerfGroup>
      <PerfGroup title="Trades">
        <PerfRow k="Total trades" v={String(num("trades") ?? "—")} />
        <PerfRow k="Win rate" v={formatPct(num("win_rate"))}
                 note={ci ? `95% CI: ${formatPct(ci[0])} – ${formatPct(ci[1])}` : undefined}
                 bar={num("win_rate")} />
        <PerfRow k="Profit factor" v={formatNum(num("profit_factor"))}
                 tone={(num("profit_factor") ?? 1) - 1} />
      </PerfGroup>
      <PerfGroup title="Dollars">
        <PerfRow k="Gross profit" v={formatNum(num("gross_profit"), 0)} tone={1} />
        <PerfRow k="Gross loss" v={formatNum(num("gross_loss"), 0)} tone={-1} />
        <PerfRow k="Net profit" v={formatNum(num("net_profit"), 0)}
                 tone={num("net_profit")} />
        {/* Visual gross-profit vs gross-loss comparison so the trader can
            see "the losers are bigger than the winners" at a glance. */}
        <ProfitLossSplit profit={num("gross_profit") ?? 0} loss={Math.abs(num("gross_loss") ?? 0)} />
      </PerfGroup>
      <PerfGroup title="Costs">
        <PerfRow k="Commission paid" v={formatNum(num("commission_paid"), 0)} />
      </PerfGroup>
    </div>
  );
}

function ProfitLossSplit({ profit, loss }: { profit: number; loss: number }) {
  const total = profit + loss;
  if (!(total > 0)) return null;
  const pPct = (profit / total) * 100;
  return (
    <div className="st-pl-split" title={`Profit ${profit.toFixed(0)} vs loss ${loss.toFixed(0)}`}>
      <div className="st-pl-bar">
        <div className="st-pl-bar-p" style={{ width: `${pPct}%` }} />
        <div className="st-pl-bar-l" style={{ width: `${100 - pPct}%` }} />
      </div>
      <div className="st-pl-legend">
        <span className="up">Profit {Math.round(pPct)}%</span>
        <span className="down">Loss {Math.round(100 - pPct)}%</span>
      </div>
    </div>
  );
}

function PerfGroup({ title, children }: {
  title: string; children: React.ReactNode;
}) {
  return (
    <div className="st-perf-group">
      <div className="st-perf-title">{title}</div>
      <div className="st-perf-rows">{children}</div>
    </div>
  );
}

function PerfRow({ k, v, note, tone, bar }: {
  k: string;
  v: string;
  note?: string;
  /** Positive paints the value green, negative red; nullish leaves it on
   *  the default foreground. Lets Breakdown surface which numbers are good
   *  and which hurt without the user having to compute signs by eye. */
  tone?: number | null;
  /** 0..1 (or 0..100 if >1) draws a thin horizontal fill under the row
   *  so the magnitude is readable at a glance. Negative values flip red. */
  bar?: number | null;
}) {
  const toneColor = tone === undefined || tone === null
    ? undefined
    : tone > 0 ? "var(--up, #22c55e)" : tone < 0 ? "var(--down, #f04452)" : undefined;
  // Normalise bar to 0..1; percentages like win_rate come as 0..1 already,
  // but a few metrics (exposure, PSR) come as 0..100 -- support both.
  const barAbs = bar === undefined || bar === null ? null : Math.abs(bar);
  const barFrac = barAbs === null ? null : barAbs > 1 ? barAbs / 100 : barAbs;
  const barColor = bar === null || bar === undefined
    ? undefined
    : bar < 0 ? "var(--down, #f04452)" : "var(--accent, #5b9dff)";
  return (
    <div className="st-perf-row">
      <div className="st-perf-k">
        {k}
        {note && <div className="st-perf-note">{note}</div>}
      </div>
      <div className="st-perf-v mono" style={toneColor ? { color: toneColor } : undefined}>{v}</div>
      {barFrac !== null && (
        <div className="st-perf-bar">
          <div className="st-perf-bar-fill"
               style={{ width: `${Math.min(100, barFrac * 100)}%`, background: barColor }} />
        </div>
      )}
    </div>
  );
}

function Metric({ k, v, tone }: { k: string; v: string; tone?: number | null }) {
  const color = tone === undefined || tone === null ? undefined
    : tone > 0 ? "var(--up)" : tone < 0 ? "var(--down)" : undefined;
  return (
    <div className="kv-cell">
      <div className="k">{k}</div>
      <div className="v" style={{ color }}>{v}</div>
    </div>
  );
}

/** Inline SVG equity curve with the drawdown envelope shaded. */
/** TradingView-style equity curve with per-trade bars underneath.
 *
 *  Top panel: the strategy's equity curve plus a running peak (the gap
 *  between them is the live drawdown) and, when a `priceSeries` is given,
 *  a buy-and-hold line normalised to the same starting capital. The
 *  buy-and-hold overlay is the single most honest thing a backtest can
 *  show -- it is the easy benchmark, and a strategy that does not clear
 *  it is not a strategy.
 *
 *  Bottom panel: a bar per trade, green up for winners and red down for
 *  losers, sized by PnL. The eye picks out the one bad month even when
 *  the smoothed curve above hides it.
 */
function EquityCurve({
  equity, timestamps, trades, priceSeries, curveStyle, setCurveStyle,
}: {
  equity: (number | null)[];
  timestamps: string[];
  trades?: BacktestPayload["trades"];
  priceSeries?: (number | null)[];
  curveStyle: "area" | "bars";
  setCurveStyle: (v: "area" | "bars") => void;
}) {
  const [showBH, setShowBH] = useState(true);
  const [hoverTrade, setHoverTrade] = useState<{
    idx: number;
    x: number;
    y: number;
  } | null>(null);
  const focusTrade = useTradeFocuser();
  const pts = equity
    .map((v, i) => ({ v, i }))
    .filter((p): p is { v: number; i: number } => typeof p.v === "number" && Number.isFinite(p.v));
  if (pts.length < 2) return null;

  const W = 900;
  const H = 220;         // top panel: equity (taller so gridlines breathe)
  const HB = 64;         // bottom panel: per-trade bars (taller for legibility)
  const GAP = 12;
  const HX = 18;         // x-axis date label strip

  // Buy-and-hold normalised to start equal to initial capital, so the two
  // lines meet at bar 0. If priceSeries is missing or empty, skip entirely.
  const start = pts[0]?.v ?? 0;
  let bhPts: { v: number; i: number }[] = [];
  if (showBH && priceSeries && priceSeries.length === equity.length) {
    const basePx = priceSeries.find((p) => typeof p === "number" && (p as number) > 0) as number | undefined;
    if (basePx && start > 0) {
      bhPts = priceSeries
        .map((p, i) => (typeof p === "number" ? { v: start * (p / basePx), i } : null))
        .filter((p): p is { v: number; i: number } => p !== null);
    }
  }

  const allY = [...pts.map((p) => p.v), ...bhPts.map((p) => p.v)];
  const lo = Math.min(...allY);
  const hi = Math.max(...allY);
  const span = hi - lo || 1;
  const xAt = (i: number) => (i / (equity.length - 1)) * W;
  const yAt = (v: number) => H - ((v - lo) / span) * (H - 8) - 4;

  const line = pts.map((p, k) => `${k === 0 ? "M" : "L"}${xAt(p.i).toFixed(1)},${yAt(p.v).toFixed(1)}`).join(" ");
  let peak = -Infinity;
  const peaks = pts.map((p) => { peak = Math.max(peak, p.v); return { i: p.i, v: peak }; });
  const peakLine = peaks.map((p, k) => `${k === 0 ? "M" : "L"}${xAt(p.i).toFixed(1)},${yAt(p.v).toFixed(1)}`).join(" ");
  const bhLine = bhPts.map((p, k) => `${k === 0 ? "M" : "L"}${xAt(p.i).toFixed(1)},${yAt(p.v).toFixed(1)}`).join(" ");

  // Per-trade bar panel. Each bar carries its trade index so the hover
  // tooltip can look it up without re-matching timestamps.
  interface BarItem { x: number; h: number; up: boolean; tradeIdx: number }
  const barItems: BarItem[] = [];
  if (trades && trades.length > 0) {
    const tsLookup = new Map<string, number>();
    timestamps.forEach((t, i) => tsLookup.set(t, i));
    const maxAbs = Math.max(...trades.map((t) => Math.abs(t.pnl)), 1);
    trades.forEach((t, k) => {
      if (!t.exit_ts) return;
      const idx = tsLookup.get(t.exit_ts);
      if (idx === undefined) return;
      const magnitude = Math.abs(t.pnl) / maxAbs;
      barItems.push({
        x: xAt(idx),
        h: Math.max(1, magnitude * (HB - 6)),
        up: t.pnl >= 0,
        tradeIdx: k,
      });
    });
  }
  const barMidY = HB / 2;
  // Direction strip dropped — it duplicated the trade-bar colour and the
  // header's long/short counters. One cleaner panel per piece of info.
  const totalH = H + GAP + HB + 4 + HX;

  // x-axis date ticks. Pick 5 roughly evenly spaced points across the
  // run and render just MMM d so the strip stays uncluttered.
  const dateTicks: { x: number; label: string }[] = [];
  if (timestamps.length > 1) {
    const n = Math.min(5, timestamps.length);
    for (let k = 0; k < n; k++) {
      const idx = Math.round((k / (n - 1)) * (timestamps.length - 1));
      const ts = timestamps[idx];
      if (!ts) continue;
      const d = new Date(ts);
      const label = d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
      dateTicks.push({ x: xAt(idx), label });
    }
  }

  const strategyFinal = pts[pts.length - 1]!.v;
  const bhFinal = bhPts.length > 0 ? bhPts[bhPts.length - 1]!.v : null;

  return (
    <div className="eq-root">
      <div className="eq-head">
        <h3 style={{ margin: 0 }}>Equity curve</h3>
        {/* Chart-style toggle lives on the toolbar now (TV-style); kept
            this spacer so the Buy & hold toggle stays on the right. */}
        <span style={{ marginRight: "auto" }} />
        {priceSeries && (
          <label className="small eq-toggle">
            <input type="checkbox" checked={showBH} onChange={(e) => setShowBH(e.target.checked)} />
            Buy &amp; hold
          </label>
        )}
      </div>
      <div style={{ overflowX: "auto" }}>
        <svg viewBox={`0 0 ${W} ${totalH}`} width="100%" height={totalH} preserveAspectRatio="none"
             style={{ display: "block", background: "var(--bg-sunken)",
                      border: "1px solid var(--border)", borderRadius: 6 }}>
          {/* equity pane — gridlines at the 25/50/75% marks make magnitude
              readable even without an axis label beside each line. */}
          {[0.25, 0.5, 0.75].map((f) => {
            const v = lo + span * f;
            return (
              <g key={f}>
                <line x1={0} x2={W} y1={yAt(v)} y2={yAt(v)}
                      stroke="var(--border)" strokeOpacity={0.4} />
                <text x={6} y={yAt(v) - 2} fill="var(--muted)"
                      fontSize="10" fontFamily="ui-sans-serif, system-ui">
                  {v.toLocaleString("en-US", { maximumFractionDigits: 0 })}
                </text>
              </g>
            );
          })}
          <line x1={0} x2={W} y1={yAt(start)} y2={yAt(start)}
                stroke="var(--border-strong)" strokeDasharray="3 3" />
          {/* Drawdown fill: the pink band between the running peak and the
              strategy equity. Reading this is "how far underwater am I" at
              every point in time -- the single most useful thing on an
              equity chart besides the line itself. */}
          {(() => {
            const ddPath = [
              ...peaks.map((p, k) => `${k === 0 ? "M" : "L"}${xAt(p.i).toFixed(1)},${yAt(p.v).toFixed(1)}`),
              ...pts.slice().reverse().map((p) => `L${xAt(p.i).toFixed(1)},${yAt(p.v).toFixed(1)}`),
              "Z",
            ].join(" ");
            return <path d={ddPath} fill="var(--down, #ea3943)" fillOpacity={0.08} />;
          })()}
          <path d={peakLine} fill="none" stroke="var(--muted)" strokeWidth={1} opacity={0.55}
                strokeDasharray="2 3" />
          {bhPts.length > 0 && (
            <path d={bhLine} fill="none" stroke="#f0b429" strokeWidth={1.4}
                  strokeDasharray="5 3" opacity={0.9} />
          )}
          {/* Area / line / bars rendering of the equity curve. Area is the
              TV default; line is cleaner on crowded screens; bars shows
              each point as its own tick for a stepped feel. */}
          {curveStyle === "area" && (
            <path
              d={`${line} L ${xAt(pts[pts.length - 1]!.i).toFixed(1)},${H - 2} L ${xAt(pts[0]!.i).toFixed(1)},${H - 2} Z`}
              fill="var(--accent)" fillOpacity={0.18}
            />
          )}
          {curveStyle === "bars" && pts.map((p, k) => (
            // Rebased against the chart floor so bars grow upward instead
            // of oscillating around the starting equity -- the latter
            // reads as a volume histogram, not an equity series.
            <line key={k}
                  x1={xAt(p.i).toFixed(1)} x2={xAt(p.i).toFixed(1)}
                  y1={(H - 2).toFixed(1)} y2={yAt(p.v).toFixed(1)}
                  stroke="var(--accent-bright, #8fbcff)" strokeWidth={0.9} opacity={0.75} />
          ))}
          {curveStyle !== "bars" && (
            <path d={line} fill="none" stroke="var(--accent-bright, #8fbcff)" strokeWidth={2} />
          )}

          {/* per-trade bars panel: one bar per trade, centerline is zero,
              green=win, red=loss, height proportional to |PnL|. Taller
              bars + a labelled centerline reads more clearly than the old
              hair-thin version. The group owns the mouseLeave so the
              tooltip stays stable while hopping between neighbouring
              bars -- the per-bar mouseLeave used to clear it between
              every two-pixel gap, which read as flicker. */}
          <g transform={`translate(0, ${H + GAP})`}
             onMouseLeave={() => setHoverTrade(null)}>
            <rect x={0} y={0} width={W} height={HB} fill="var(--bg)" fillOpacity={0.4}
                  stroke="var(--border)" strokeOpacity={0.5} />
            {/* +PnL / -PnL row labels on the far left so the panel is
                legible without a legend elsewhere. */}
            <text x={6} y={10} fill="var(--up, #16c784)" fontSize="9"
                  fontFamily="ui-sans-serif, system-ui" opacity={0.75}>+PnL</text>
            <text x={6} y={HB - 3} fill="var(--down, #ea3943)" fontSize="9"
                  fontFamily="ui-sans-serif, system-ui" opacity={0.75}>−PnL</text>
            <line x1={0} x2={W} y1={barMidY} y2={barMidY} stroke="var(--border-strong)" opacity={0.7} />
            {barItems.map((b, k) => (
              <rect
                key={k}
                x={b.x - 1.5}
                width={3}
                y={b.up ? barMidY - b.h : barMidY}
                height={Math.max(1, b.h)}
                fill={b.up ? "var(--up, #16c784)" : "var(--down, #ea3943)"}
                opacity={hoverTrade?.idx === b.tradeIdx ? 1 : 0.9}
                stroke={hoverTrade?.idx === b.tradeIdx ? "#fff" : "none"}
                strokeWidth={0.6}
                rx={0.6}
                style={{ cursor: "pointer" }}
                onMouseEnter={(e) => {
                  const svg = (e.currentTarget.ownerSVGElement as SVGSVGElement | null);
                  const rect = svg?.getBoundingClientRect();
                  if (!rect) return;
                  setHoverTrade({
                    idx: b.tradeIdx,
                    x: e.clientX - rect.left,
                    y: e.clientY - rect.top,
                  });
                }}
                onClick={() => {
                  // "Click to show on chart" -- same behaviour TV offers in
                  // its per-trade tooltip. Jumps the chart to the trade's
                  // exit bar.
                  const trade = trades?.[b.tradeIdx];
                  if (!trade?.exit_ts) return;
                  focusTrade({
                    time: Math.floor(new Date(trade.exit_ts).getTime() / 1000),
                    side: "exit",
                    tradeNumber: b.tradeIdx + 1,
                  });
                }}
              />
            ))}
          </g>

          {/* x-axis date ticks: 5 roughly-evenly-spaced MMM d labels so
              scanning the equity curve to a time point doesn't require
              hovering over every bar. */}
          <g transform={`translate(0, ${H + GAP + HB + 4})`}>
            {dateTicks.map((tk, k) => {
              const align = k === 0 ? "start" : k === dateTicks.length - 1 ? "end" : "middle";
              return (
                <text
                  key={k}
                  x={tk.x}
                  y={12}
                  fill="var(--muted)"
                  fontSize="10"
                  fontFamily="ui-sans-serif, system-ui"
                  textAnchor={align}
                >{tk.label}</text>
              );
            })}
          </g>
        </svg>
        {hoverTrade && trades && trades[hoverTrade.idx] && (() => {
          const t = trades[hoverTrade.idx]!;
          return (
            <div
              className="eq-tooltip"
              style={{ left: hoverTrade.x + 12, top: Math.max(hoverTrade.y - 70, 0) }}
            >
              <div className="eq-tooltip-head">
                Trade {hoverTrade.idx + 1}{" "}
                <span className={t.direction === "long" ? "up" : "down"}>
                  {t.direction}
                </span>
              </div>
              <div className="eq-tooltip-row">
                <span>PnL</span>
                <span className={t.pnl >= 0 ? "up" : "down"}>
                  {t.pnl >= 0 ? "+" : ""}{formatNum(t.pnl, 2)}
                </span>
              </div>
              <div className="eq-tooltip-row">
                <span>Return</span>
                <span className={t.pnl_pct >= 0 ? "up" : "down"}>
                  {t.pnl_pct >= 0 ? "+" : ""}{formatPct(t.pnl_pct, 2)}
                </span>
              </div>
              <div className="eq-tooltip-row">
                <span>Bars held</span>
                <span>{t.bars_held}</span>
              </div>
              {typeof t.mfe === "number" && (
                <div className="eq-tooltip-row">
                  <span title="Max favorable excursion -- how far the trade went in your favour before you exited">
                    Favorable
                  </span>
                  <span className="up">+{formatPct(t.mfe, 2)}</span>
                </div>
              )}
              {typeof t.mae === "number" && (
                <div className="eq-tooltip-row">
                  <span title="Max adverse excursion -- how far it went against you before the exit">
                    Adverse
                  </span>
                  <span className="down">{formatPct(t.mae, 2)}</span>
                </div>
              )}
              {t.exit_ts && (
                <div className="eq-tooltip-row">
                  <span>Closed</span>
                  <span className="mono">{t.exit_ts.slice(0, 10)}</span>
                </div>
              )}
              {t.exit_reason && (
                <div className="eq-tooltip-row faint small">
                  <span>Why out</span><span>{t.exit_reason}</span>
                </div>
              )}
            </div>
          );
        })()}
      </div>
      <div className="faint small mono">
        {timestamps[0]?.slice(0, 10)} → {timestamps[timestamps.length - 1]?.slice(0, 10)}
        {" · "}grey = running peak (gap below = drawdown)
        {bhPts.length > 0 && (
          <>
            {" · "}
            <span style={{ color: "#f0b429" }}>yellow dashed = buy &amp; hold</span>
            {bhFinal !== null && (
              <> · final: strategy {formatNum(strategyFinal, 0)} vs B&amp;H {formatNum(bhFinal, 0)}
                {" "}({strategyFinal > bhFinal ? "beat" : "lagged"})</>
            )}
          </>
        )}
      </div>
    </div>
  );
}

/** TradingView-style long/short split bar: two full-width horizontal bars
 *  side by side showing where PnL came from. */
function PnlBySide({ long, short }: {
  long: { trades: number; pnl: number | null; win_rate: number | null };
  short: { trades: number; pnl: number | null; win_rate: number | null };
}) {
  const lp = long.pnl ?? 0;
  const sp = short.pnl ?? 0;
  const total = Math.abs(lp) + Math.abs(sp) || 1;
  return (
    <div className="pls-root">
      <div className="pls-head">Profits and losses · by side</div>
      {[
        { label: "Long", d: long, pnl: lp },
        { label: "Short", d: short, pnl: sp },
      ].map((row) => (
        <div className="pls-row" key={row.label}>
          <div className="pls-label">
            {row.label} <span className="faint">· {row.d.trades} trades</span>
          </div>
          <div className="pls-track">
            <div
              className="pls-fill"
              style={{
                width: `${(Math.abs(row.pnl) / total) * 100}%`,
                background: row.pnl >= 0 ? "var(--up, #16c784)" : "var(--down, #ea3943)",
              }}
            />
          </div>
          <div className={`pls-amount mono ${row.pnl >= 0 ? "up" : "down"}`}>
            {row.pnl >= 0 ? "+" : ""}{formatNum(row.pnl, 0)}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── TradingView-style Performance sub-tabs ──────────────────────────

/** Reusable bar-chart renderer for periodic PnL and benchmarking views. */
function PeriodicalBars({ bars, height = 220 }: {
  bars: Array<{ label: string; a: number | null; b?: number | null; colorA?: string; colorB?: string }>;
  height?: number;
}) {
  if (bars.length === 0) {
    return <div className="st-empty">No data in the selected period.</div>;
  }
  const W = 900;
  const H = height;
  const vals: number[] = [];
  for (const r of bars) {
    if (typeof r.a === "number") vals.push(r.a);
    if (typeof r.b === "number") vals.push(r.b);
  }
  const lo = Math.min(0, ...vals);
  const hi = Math.max(0, ...vals);
  const span = hi - lo || 1;
  const zero = H - ((0 - lo) / span) * (H - 20) - 10;
  const colW = W / bars.length;
  const barW = Math.max(2, (colW * 0.7) / (bars[0]!.b !== undefined ? 2 : 1));
  const yAt = (v: number) => H - ((v - lo) / span) * (H - 20) - 10;

  return (
    <div style={{ overflowX: "auto" }}>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} preserveAspectRatio="none"
           style={{ display: "block", background: "var(--bg-sunken)",
                    border: "1px solid var(--border)", borderRadius: 6 }}>
        <line x1={0} x2={W} y1={zero} y2={zero} stroke="var(--border-strong)" />
        {bars.map((r, i) => {
          const cx = colW * (i + 0.5);
          const a = r.a ?? 0;
          const aY = yAt(a);
          const bFill = r.colorB ?? "#8d9bb0";
          const aFill = r.colorA ?? (a >= 0 ? "var(--up, #16c784)" : "var(--down, #ea3943)");
          if (r.b === undefined) {
            return (
              <rect key={i} x={cx - barW / 2} width={barW}
                    y={Math.min(aY, zero)} height={Math.abs(aY - zero)}
                    fill={aFill} opacity={0.9} />
            );
          }
          const b = r.b ?? 0;
          const bY = yAt(b);
          return (
            <g key={i}>
              <rect x={cx - barW - 1} width={barW}
                    y={Math.min(aY, zero)} height={Math.abs(aY - zero)}
                    fill={aFill} opacity={0.9} />
              <rect x={cx + 1} width={barW}
                    y={Math.min(bY, zero)} height={Math.abs(bY - zero)}
                    fill={bFill} opacity={0.9} />
            </g>
          );
        })}
      </svg>
      <div className="st-xaxis">
        {bars.map((r, i) => (
          <span key={i} style={{ width: `${100 / bars.length}%` }}>{r.label}</span>
        ))}
      </div>
    </div>
  );
}

function PeriodicalView({ m }: { m: BacktestPayload["metrics"] }) {
  type P = "daily" | "weekly" | "monthly" | "quarterly" | "yearly";
  const [period, setPeriod] = useState<P>("monthly");
  const periodical = (m.periodical as unknown) as {
    daily: Array<{ label: string; pnl: number | null; pnl_pct: number | null }>;
    weekly: typeof periodicalWeekly;
    monthly: typeof periodicalMonthly;
    quarterly: typeof periodicalQuarterly;
    yearly: typeof periodicalYearly;
  } | undefined;

  const rows = periodical?.[period] ?? [];
  const bars = rows.map((r) => ({ label: r.label, a: r.pnl }));

  return (
    <div className="st-perf-pane">
      <div className="st-perf-head">
        <h4 style={{ margin: 0 }}>{period[0]!.toUpperCase() + period.slice(1)} PnL</h4>
        <div className="st-chip-row">
          {(["daily", "weekly", "monthly", "quarterly", "yearly"] as const).map((p) => (
            <button
              key={p}
              className={`st-chip ${period === p ? "st-chip-on" : ""}`}
              onClick={() => setPeriod(p)}
            >{p[0]!.toUpperCase() + p.slice(1)}</button>
          ))}
        </div>
      </div>
      <PeriodicalBars bars={bars} height={220} />
    </div>
  );
}
// Type shims so the periodical cast doesn't need its full inline shape
const periodicalWeekly: Array<{ label: string; pnl: number | null; pnl_pct: number | null }> = [];
const periodicalMonthly = periodicalWeekly;
const periodicalQuarterly = periodicalWeekly;
const periodicalYearly = periodicalWeekly;

function BenchmarkingView({ m }: { m: BacktestPayload["metrics"] }) {
  type P = "weekly" | "monthly";
  const [period, setPeriod] = useState<P>("weekly");
  const bm = (m.benchmarking as unknown) as {
    available: boolean;
    strategy_return: number | null;
    bh_return: number | null;
    outperformance: number | null;
    correlation: number | null;
    weekly: Array<{ label: string; strategy_pnl: number | null; bh_pnl: number | null }>;
    monthly: Array<{ label: string; strategy_pnl: number | null; bh_pnl: number | null }>;
  } | undefined;

  if (!bm?.available) {
    return <div className="st-empty">Benchmarking requires the symbol's price series. Re-run the backtest.</div>;
  }

  const rows = bm[period] ?? [];
  const bars = rows.map((r) => ({
    label: r.label,
    a: r.strategy_pnl,
    b: r.bh_pnl,
    colorA: "#4d8dff",
    colorB: "#8d9bb0",
  }));

  return (
    <div className="st-perf-pane">
      <div className="st-perf-headline">
        <HeadlineStat label="Strategy return" value={formatPct(bm.strategy_return)} tone={bm.strategy_return} />
        <HeadlineStat label="Buy and hold return" value={formatPct(bm.bh_return)} tone={bm.bh_return} />
        <HeadlineStat label="Outperformance" value={formatPct(bm.outperformance)} tone={bm.outperformance} />
        <HeadlineStat label="Correlation" value={bm.correlation !== null ? formatNum(bm.correlation, 2) : "—"} />
      </div>
      <div className="st-perf-head">
        <h4 style={{ margin: 0 }}>Strategy vs benchmark</h4>
        <div className="st-chip-row">
          {(["weekly", "monthly"] as const).map((p) => (
            <button
              key={p}
              className={`st-chip ${period === p ? "st-chip-on" : ""}`}
              onClick={() => setPeriod(p)}
            >{p[0]!.toUpperCase() + p.slice(1)}</button>
          ))}
        </div>
      </div>
      <PeriodicalBars bars={bars} height={220} />
      <div className="faint small" style={{ display: "flex", gap: 16, justifyContent: "center", marginTop: 4 }}>
        <span><span className="legend-dot" style={{ background: "#4d8dff" }} /> Strategy PnL</span>
        <span><span className="legend-dot" style={{ background: "#8d9bb0" }} /> Buy and hold PnL</span>
      </div>
    </div>
  );
}

function GrowthDeclineView({ m }: { m: BacktestPayload["metrics"] }) {
  const gd = (m.growth_decline as unknown) as {
    periods: Array<{ type: "up" | "down"; duration_bars: number; change_pct: number | null;
                     start_label: string; end_label: string }>;
    run_up: { max: number | null; avg: number | null; current: number | null };
    drawdown: { max: number | null; avg: number | null; current: number | null };
  } | undefined;

  if (!gd || gd.periods.length === 0) {
    return <div className="st-empty">Not enough equity history to measure growth and decline periods.</div>;
  }

  // "Alternating growth and decline" bars — each period as one column.
  const bars = gd.periods.map((p) => ({
    label: `${p.start_label.slice(5)}`,
    a: (p.change_pct ?? 0) * (p.type === "up" ? 1 : -1),
    colorA: p.type === "up" ? "var(--up, #16c784)" : "var(--down, #ea3943)",
  }));

  // Comparison bars (horizontal max / avg / current).
  const scale = Math.max(gd.run_up.max ?? 0, gd.drawdown.max ?? 0, 0.01);
  const HBar = ({ v, color }: { v: number | null; color: string }) => (
    <div className="pls-track">
      <div className="pls-fill" style={{ width: `${((v ?? 0) / scale) * 100}%`, background: color }} />
    </div>
  );

  return (
    <div className="st-perf-pane">
      <div className="st-perf-headline">
        <HeadlineStat label="Max run-up" value={formatPct(gd.run_up.max)} tone={1} />
        <HeadlineStat label="Avg run-up" value={formatPct(gd.run_up.avg)} />
        <HeadlineStat label="Max drawdown" value={formatPct(gd.drawdown.max)} tone={-1} />
        <HeadlineStat label="Avg drawdown" value={formatPct(gd.drawdown.avg)} />
      </div>
      <h4>Alternating growth and decline</h4>
      <PeriodicalBars bars={bars} height={200} />
      <h4 style={{ marginTop: 16 }}>Comparison of periods</h4>
      <div className="pls-root">
        <div className="pls-head">Run-up</div>
        <div className="pls-row"><div className="pls-label">Maximum</div>
          <HBar v={gd.run_up.max} color="var(--up, #16c784)" />
          <div className="pls-amount mono up">{formatPct(gd.run_up.max)}</div></div>
        <div className="pls-row"><div className="pls-label">Average</div>
          <HBar v={gd.run_up.avg} color="var(--up, #16c784)" />
          <div className="pls-amount mono up">{formatPct(gd.run_up.avg)}</div></div>
        {gd.run_up.current !== null && (
          <div className="pls-row"><div className="pls-label">Current</div>
            <HBar v={gd.run_up.current} color="var(--up, #16c784)" />
            <div className="pls-amount mono up">{formatPct(gd.run_up.current)}</div></div>
        )}
      </div>
      <div className="pls-root" style={{ marginTop: 8 }}>
        <div className="pls-head">Drawdown</div>
        <div className="pls-row"><div className="pls-label">Maximum</div>
          <HBar v={gd.drawdown.max} color="var(--down, #ea3943)" />
          <div className="pls-amount mono down">{formatPct(gd.drawdown.max)}</div></div>
        <div className="pls-row"><div className="pls-label">Average</div>
          <HBar v={gd.drawdown.avg} color="var(--down, #ea3943)" />
          <div className="pls-amount mono down">{formatPct(gd.drawdown.avg)}</div></div>
      </div>
    </div>
  );
}

// ── Trades Analysis (Distribution / Streaks / Time patterns) ───────

function TradesAnalysis({ m, trades }: {
  m: BacktestPayload["metrics"];
  trades: BacktestPayload["trades"];
}) {
  type Sub = "distribution" | "streaks" | "time";
  const [sub, setSub] = usePersisted<Sub>("tt.bt.trsub", "distribution");

  return (
    <>
      <div className="st-subtabs" role="tablist">
        {([
          ["distribution", "Distribution"],
          ["streaks", "Streaks"],
          ["time", "Time patterns"],
        ] as const).map(([key, label]) => (
          <button
            key={key}
            className={`st-subtab ${sub === key ? "st-subtab-on" : ""}`}
            onClick={() => setSub(key)}
          >{label}</button>
        ))}
      </div>

      {sub === "distribution" && <DistributionView m={m} trades={trades} />}
      {sub === "streaks" && <StreaksView m={m} />}
      {sub === "time" && <TimePatternsView m={m} />}
    </>
  );
}

function DistributionView({ m, trades }: {
  m: BacktestPayload["metrics"];
  trades: BacktestPayload["trades"];
}) {
  const dist = (m.distribution as unknown) as {
    bins: Array<{ range_lo: number; range_hi: number; winners: number; losers: number }>;
    winners: number; losers: number; breakevens: number;
    expectancy_pct: number | null;
    avg_win_pct: number | null;
    avg_loss_pct: number | null;
  } | undefined;

  if (!dist || dist.bins.length === 0) {
    return <div className="st-empty">No trades to analyse.</div>;
  }

  const total = dist.winners + dist.losers + dist.breakevens;
  const W = 900;
  const H = 220;
  const maxCount = Math.max(...dist.bins.map((b) => b.winners + b.losers), 1);
  const colW = W / dist.bins.length;
  const barW = colW * 0.72;

  // Donut chart (winners/losers/breakevens)
  const CENTER = 70;
  const RADIUS = 50;
  const STROKE = 18;
  const circumference = 2 * Math.PI * RADIUS;
  const winFrac = dist.winners / total;
  const loseFrac = dist.losers / total;
  // breakFrac = whatever's left; drawn implicitly as the gap

  return (
    <div className="st-perf-pane">
      <div className="st-perf-headline">
        <HeadlineStat label="Expectancy" value={formatPct(dist.expectancy_pct, 2)} />
        <HeadlineStat label="Avg win" value={formatPct(dist.avg_win_pct, 2)} tone={1} />
        <HeadlineStat label="Avg loss" value={formatPct(dist.avg_loss_pct, 2)} tone={-1} />
        <HeadlineStat label="Total" value={String(total)} />
      </div>

      <h4>Returns distribution</h4>
      <div style={{ overflowX: "auto" }}>
        <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} preserveAspectRatio="none"
             style={{ display: "block", background: "var(--bg-sunken)",
                      border: "1px solid var(--border)", borderRadius: 6 }}>
          {dist.bins.map((b, i) => {
            const cx = colW * (i + 0.5);
            const totalBar = b.winners + b.losers;
            const h = (totalBar / maxCount) * (H - 30);
            const color = b.winners > b.losers ? "var(--up, #16c784)" : "var(--down, #ea3943)";
            return (
              <g key={i}>
                <rect x={cx - barW / 2} width={barW}
                      y={H - 20 - h} height={h}
                      fill={color} opacity={0.85} />
              </g>
            );
          })}
          <line x1={0} x2={W} y1={H - 20} y2={H - 20} stroke="var(--border-strong)" />
        </svg>
        <div className="st-xaxis">
          {dist.bins.map((b, i) => (
            <span key={i} style={{ width: `${100 / dist.bins.length}%` }}>
              {Math.round(b.range_lo * 100)}%
            </span>
          ))}
        </div>
      </div>

      <h4 style={{ marginTop: 16 }}>Trades distribution</h4>
      <div style={{ display: "flex", alignItems: "center", gap: 24 }}>
        <svg width={140} height={140} viewBox="0 0 140 140">
          {/* Full ring = losers, winner slice on top */}
          <circle cx={CENTER} cy={CENTER} r={RADIUS} fill="none"
                  stroke="var(--down, #ea3943)" strokeWidth={STROKE} opacity={0.85} />
          <circle cx={CENTER} cy={CENTER} r={RADIUS} fill="none"
                  stroke="var(--up, #16c784)" strokeWidth={STROKE} opacity={0.95}
                  strokeDasharray={`${circumference * winFrac} ${circumference}`}
                  transform={`rotate(-90 ${CENTER} ${CENTER})`} />
          <text x={CENTER} y={CENTER - 2} fontSize={20} fontWeight={600}
                textAnchor="middle" fill="var(--text)">{total}</text>
          <text x={CENTER} y={CENTER + 14} fontSize={9}
                textAnchor="middle" fill="var(--muted)">Total trades</text>
        </svg>
        <div className="st-donut-key">
          <div><span className="legend-dot" style={{ background: "var(--up, #16c784)" }} /> Winners
            <span className="mono"> {dist.winners} trades · {formatPct(winFrac)}</span></div>
          <div><span className="legend-dot" style={{ background: "var(--down, #ea3943)" }} /> Losers
            <span className="mono"> {dist.losers} trades · {formatPct(loseFrac)}</span></div>
          <div><span className="legend-dot" style={{ background: "#8d9bb0" }} /> Breakevens
            <span className="mono"> {dist.breakevens} trades · {formatPct(dist.breakevens / total)}</span></div>
        </div>
      </div>

      {/* No-op reference, so TS doesn't flag `trades` prop as unused. */}
      <div style={{ display: "none" }}>{trades.length}</div>
    </div>
  );
}

function StreaksView({ m }: { m: BacktestPayload["metrics"] }) {
  const streaks = (m.streaks as unknown) as {
    series: Array<{ dir: "win" | "loss"; count: number; pnl: number | null }>;
    longest_win: number;
    longest_loss: number;
  } | undefined;

  if (!streaks || streaks.series.length === 0) {
    return <div className="st-empty">No streaks yet — the strategy has not produced two trades in a row of the same direction.</div>;
  }

  const [mode, setMode] = useState<"count" | "amount">("count");
  const W = 900;
  const H = 220;
  const vals = streaks.series.map((s) =>
    (mode === "count" ? s.count : Math.abs(s.pnl ?? 0)) * (s.dir === "win" ? 1 : -1),
  );
  const hi = Math.max(0, ...vals);
  const lo = Math.min(0, ...vals);
  const span = hi - lo || 1;
  const colW = W / streaks.series.length;
  const barW = Math.max(1.5, colW * 0.6);
  const zero = H - ((0 - lo) / span) * (H - 20) - 10;
  const yAt = (v: number) => H - ((v - lo) / span) * (H - 20) - 10;

  return (
    <div className="st-perf-pane">
      <div className="st-perf-headline">
        <HeadlineStat label="Longest winning streak" value={`${streaks.longest_win} trades`} tone={1} />
        <HeadlineStat label="Longest losing streak" value={`${streaks.longest_loss} trades`} tone={-1} />
      </div>
      <div className="st-perf-head">
        <h4 style={{ margin: 0 }}>Winning and losing streaks</h4>
        <div className="st-chip-row">
          <button className={`st-chip ${mode === "count" ? "st-chip-on" : ""}`}
                  onClick={() => setMode("count")}>Count</button>
          <button className={`st-chip ${mode === "amount" ? "st-chip-on" : ""}`}
                  onClick={() => setMode("amount")}>Amount</button>
        </div>
      </div>
      <div style={{ overflowX: "auto" }}>
        <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} preserveAspectRatio="none"
             style={{ display: "block", background: "var(--bg-sunken)",
                      border: "1px solid var(--border)", borderRadius: 6 }}>
          <line x1={0} x2={W} y1={zero} y2={zero} stroke="var(--border-strong)" />
          {streaks.series.map((s, i) => {
            const v = (mode === "count" ? s.count : Math.abs(s.pnl ?? 0)) * (s.dir === "win" ? 1 : -1);
            const yv = yAt(v);
            return (
              <rect key={i}
                    x={colW * (i + 0.5) - barW / 2}
                    width={barW}
                    y={Math.min(yv, zero)}
                    height={Math.abs(yv - zero)}
                    fill={s.dir === "win" ? "var(--up, #16c784)" : "var(--down, #ea3943)"}
                    opacity={0.9} />
            );
          })}
        </svg>
      </div>
      <div className="faint small" style={{ display: "flex", gap: 16, justifyContent: "center" }}>
        <span><span className="legend-dot" style={{ background: "var(--up, #16c784)" }} /> Winners</span>
        <span><span className="legend-dot" style={{ background: "var(--down, #ea3943)" }} /> Losers</span>
      </div>
    </div>
  );
}

function TimePatternsView({ m }: { m: BacktestPayload["metrics"] }) {
  const tp = (m.time_patterns as unknown) as {
    by_dow: Array<{ label: string; winners: number; losers: number }>;
    by_month: Array<{ label: string; winners: number; losers: number }>;
  } | undefined;

  if (!tp) return <div className="st-empty">No time pattern data.</div>;

  const [bucket, setBucket] = useState<"days" | "months">("days");
  const rows = bucket === "days" ? tp.by_dow : tp.by_month;

  const W = 900;
  const H = 220;
  const maxTotal = Math.max(...rows.map((r) => r.winners + r.losers), 1);
  const colW = W / rows.length;
  const barW = colW * 0.72;

  return (
    <div className="st-perf-pane">
      <div className="st-perf-head">
        <h4 style={{ margin: 0 }}>Results by time</h4>
        <div className="st-chip-row">
          <button className={`st-chip ${bucket === "days" ? "st-chip-on" : ""}`}
                  onClick={() => setBucket("days")}>Days</button>
          <button className={`st-chip ${bucket === "months" ? "st-chip-on" : ""}`}
                  onClick={() => setBucket("months")}>Months</button>
        </div>
      </div>
      <div style={{ overflowX: "auto" }}>
        <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} preserveAspectRatio="none"
             style={{ display: "block", background: "var(--bg-sunken)",
                      border: "1px solid var(--border)", borderRadius: 6 }}>
          {rows.map((r, i) => {
            const totalBar = r.winners + r.losers;
            const totalH = (totalBar / maxTotal) * (H - 30);
            const winnerH = totalBar ? totalH * (r.winners / totalBar) : 0;
            const loserH = totalH - winnerH;
            const cx = colW * (i + 0.5);
            return (
              <g key={i}>
                <rect x={cx - barW / 2} width={barW}
                      y={H - 20 - totalH} height={loserH}
                      fill="var(--down, #ea3943)" opacity={0.85} />
                <rect x={cx - barW / 2} width={barW}
                      y={H - 20 - winnerH} height={winnerH}
                      fill="var(--up, #16c784)" opacity={0.85} />
              </g>
            );
          })}
          <line x1={0} x2={W} y1={H - 20} y2={H - 20} stroke="var(--border-strong)" />
        </svg>
        <div className="st-xaxis">
          {rows.map((r, i) => (
            <span key={i} style={{ width: `${100 / rows.length}%` }}>{r.label}</span>
          ))}
        </div>
      </div>
      <div className="faint small" style={{ display: "flex", gap: 16, justifyContent: "center" }}>
        <span><span className="legend-dot" style={{ background: "var(--up, #16c784)" }} /> Winners</span>
        <span><span className="legend-dot" style={{ background: "var(--down, #ea3943)" }} /> Losers</span>
      </div>
    </div>
  );
}

// ── TradingView-style Strategy Settings dialog ──────────────────────

function StrategySettings(props: {
  open: boolean;
  onClose: () => void;
  capital: number; setCapital: (n: number) => void;
  qtyValue: number; setQtyValue: (n: number) => void;
  slippage: number; setSlippage: (n: number) => void;
  commission: number; setCommission: (n: number) => void;
  allowShort: boolean; setAllowShort: (b: boolean) => void;
  pyramiding: number; setPyramiding: (n: number) => void;
  longLev: number; setLongLev: (n: number) => void;
  shortLev: number; setShortLev: (n: number) => void;
  orderDelay: number; setOrderDelay: (n: number) => void;
  limitExec: "requested_price" | "allow_slippage"; setLimitExec: (v: "requested_price" | "allow_slippage") => void;
  startDate: string; setStartDate: (v: string) => void;
  endDate: string; setEndDate: (v: string) => void;
  detalization: "default" | "high"; setDetalization: (v: "default" | "high") => void;
  bars: number; setBars: (n: number) => void;
  scriptTitle: string;
}) {
  type SubTab = "inputs" | "properties" | "style" | "visibility";
  const [sub, setSub] = useState<SubTab>("properties");

  const closeRef = useRef(props.onClose);
  closeRef.current = props.onClose;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") closeRef.current(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <motion.div
      className="st-settings-backdrop"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.16 }}
      onPointerDown={(e) => {
        if (e.target === e.currentTarget) props.onClose();
      }}
    >
      <motion.div
        className="st-settings"
        initial={{ opacity: 0, y: 8, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 8, scale: 0.98 }}
        transition={{ type: "spring", stiffness: 420, damping: 32, mass: 0.6 }}
      >
        <div className="st-settings-head">
          <span className="st-settings-title">{props.scriptTitle}</span>
          <button className="mc-close" onClick={props.onClose} title="Close (Esc)">×</button>
        </div>

        <div className="st-settings-tabs">
          {([
            ["inputs", "Inputs"],
            ["properties", "Properties"],
            ["style", "Style"],
            ["visibility", "Visibility"],
          ] as const).map(([key, label]) => (
            <button
              key={key}
              className={`st-settings-tab ${sub === key ? "st-settings-tab-on" : ""}`}
              onClick={() => setSub(key)}
            >{label}</button>
          ))}
        </div>

        <div className="st-settings-body">
          {sub === "inputs" && (
            <div className="st-settings-empty">
              The script's inputs show up in the Pine editor's left column.
              Save and run the script there to tune them.
            </div>
          )}

          {sub === "properties" && (
            <>
              <div className="st-settings-section">GENERAL</div>
              <div className="st-settings-row">
                <label>Initial capital</label>
                <div className="st-settings-pair">
                  <input type="number" value={props.capital}
                         onChange={(e) => props.setCapital(Number(e.target.value))} />
                  <select defaultValue="USD" disabled title="Only USD is supported today">
                    <option value="USD">USD</option>
                  </select>
                </div>
              </div>
              <div className="st-settings-row">
                <label>Default order size</label>
                <div className="st-settings-pair">
                  <input type="number" value={props.qtyValue} min={1} max={100}
                         onChange={(e) => props.setQtyValue(Number(e.target.value))} />
                  <span className="st-settings-unit">% of equity</span>
                </div>
              </div>
              <div className="st-settings-row">
                <label title="How many additional entries are allowed in the same direction before closing. 0 means one open position per side.">Pyramiding</label>
                <input type="number" value={props.pyramiding} min={0} max={10}
                       onChange={(e) => props.setPyramiding(Math.max(0, Math.min(10, Number(e.target.value))))} />
              </div>
              <div className="st-settings-row">
                <label>Bars loaded</label>
                <input type="number" value={props.bars} min={BARS_MIN} max={BARS_MAX}
                       onChange={(e) => props.setBars(clampBars(Number(e.target.value)))} />
              </div>

              <div className="st-settings-section">DETALIZATION AND EXECUTION</div>
              <div className="st-settings-row">
                <label>Bar detalization</label>
                <select value={props.detalization}
                        onChange={(e) => props.setDetalization(e.target.value as "default" | "high")}>
                  <option value="default">Default (next bar open)</option>
                  <option value="high">High (signal bar close)</option>
                </select>
              </div>
              <div className="st-settings-row">
                <label>Script execution</label>
                <select defaultValue="close" disabled title="Fixed in the current engine">
                  <option value="close">On bar close</option>
                </select>
              </div>

              <div className="st-settings-section">BROKER EMULATOR</div>
              <div className="st-settings-row">
                <label>Commission</label>
                <div className="st-settings-pair">
                  <input type="number" value={props.commission} min={0} step={0.005}
                         onChange={(e) => props.setCommission(Number(e.target.value))} />
                  <select defaultValue="percent" disabled>
                    <option value="percent">Percent</option>
                  </select>
                </div>
              </div>
              <div className="st-settings-row">
                <label>Long leverage</label>
                <div className="st-settings-pair">
                  <input type="number" value={props.longLev || ""} placeholder="∞"
                         min={0} step={0.5}
                         onChange={(e) => props.setLongLev(Number(e.target.value) || 0)} />
                  <span className="st-settings-unit">× (0 = ∞)</span>
                </div>
              </div>
              <div className="st-settings-row">
                <label>Short leverage</label>
                <div className="st-settings-pair">
                  <input type="number" value={props.shortLev || ""} placeholder="∞"
                         min={0} step={0.5}
                         onChange={(e) => props.setShortLev(Number(e.target.value) || 0)} />
                  <span className="st-settings-unit">× (0 = ∞)</span>
                </div>
              </div>
              <div className="st-settings-row">
                <label>Slippage</label>
                <div className="st-settings-pair">
                  <input type="number" value={props.slippage} min={0} step={0.5}
                         onChange={(e) => props.setSlippage(Number(e.target.value))} />
                  <span className="st-settings-unit">bps</span>
                </div>
              </div>
              <div className="st-settings-row">
                <label>Order execution delay</label>
                <div className="st-settings-pair">
                  <input type="number" value={props.orderDelay} min={0} max={10} step={1}
                         onChange={(e) => props.setOrderDelay(Number(e.target.value))} />
                  <span className="st-settings-unit">bars</span>
                </div>
              </div>
              <div className="st-settings-row">
                <label>Limit order execution</label>
                <select value={props.limitExec}
                        onChange={(e) => props.setLimitExec(e.target.value as "requested_price" | "allow_slippage")}>
                  <option value="requested_price">Requested price</option>
                  <option value="allow_slippage">Allow slippage</option>
                </select>
              </div>
              <div className="st-settings-row">
                <label>Allow short</label>
                <input type="checkbox" checked={props.allowShort}
                       onChange={(e) => props.setAllowShort(e.target.checked)} />
              </div>

              <div className="st-settings-section">TESTING PERIOD</div>
              <div className="st-settings-row">
                <label>Start date</label>
                <input type="date" value={props.startDate}
                       onChange={(e) => props.setStartDate(e.target.value)} />
              </div>
              <div className="st-settings-row">
                <label>End date</label>
                <input type="date" value={props.endDate}
                       onChange={(e) => props.setEndDate(e.target.value)} />
              </div>
              <div className="st-settings-row">
                <label></label>
                <button
                  className="btn"
                  onClick={() => { props.setStartDate(""); props.setEndDate(""); }}
                  style={{ justifySelf: "start" }}
                >Clear custom range</button>
              </div>
            </>
          )}

          {sub === "style" && (
            <>
              <div className="st-settings-section">ON-CHART ANNOTATIONS</div>
              <StyleToggleRow
                label="Trades on chart"
                storageKey="tt.bt.style.trades"
                defaultValue={true}
                hint="Draws arrows at each entry and exit on the price chart."
              />
              <StyleToggleRow
                label="Signal labels"
                storageKey="tt.bt.style.signals"
                defaultValue={true}
                hint="The script's own labels placed via strategy.entry(...) / strategy.exit(...)."
              />
              <StyleToggleRow
                label="Quantity"
                storageKey="tt.bt.style.qty"
                defaultValue={true}
                hint="Order size shown on each marker."
              />
              <div className="st-settings-section">OUTPUT VALUES</div>
              <StyleToggleRow
                label="Labels on price scale"
                storageKey="tt.bt.style.price_labels"
                defaultValue={true}
                hint="Current value of each plot on the right-hand price axis."
              />
              <StyleToggleRow
                label="Values in status line"
                storageKey="tt.bt.style.status_line"
                defaultValue={true}
                hint="Each plot's current value in the chart's status row."
              />
            </>
          )}
          {sub === "visibility" && (
            <>
              <div className="st-settings-section">TIMEFRAMES</div>
              {([
                "1m", "5m", "15m", "30m", "1h", "4h", "1d", "1w",
              ] as const).map((tf) => (
                <StyleToggleRow
                  key={tf}
                  label={tf}
                  storageKey={`tt.bt.vis.${tf}`}
                  defaultValue={true}
                  hint={`Show the strategy on the ${tf} chart.`}
                />
              ))}
            </>
          )}
        </div>

        <div className="st-settings-foot">
          <button className="btn" onClick={props.onClose}>Close</button>
        </div>
      </motion.div>
    </motion.div>
  );
}

/** Trade list, TradingView-style.
 *
 *  Each trade is one grouped block with an exit row above an entry row.
 *  Trade number is the stable row key for the group. Newest first (TV
 *  default). Columns: Trade number · Type · Date and time · Price · Size ·
 *  Net PnL · Return.
 */
function TradesTable({ trades }: { trades: BacktestPayload["trades"] }) {
  const focusTrade = useTradeFocuser();
  const click = (t: BacktestPayload["trades"][number], i: number, side: "entry" | "exit") => {
    const ts = side === "exit" ? (t.exit_ts ?? t.entry_ts) : t.entry_ts;
    if (!ts) return;
    focusTrade({
      time: Math.floor(new Date(ts).getTime() / 1000),
      side,
      tradeNumber: i + 1,
    });
  };

  // Newest first, like TradingView. Preserve the trade number from the
  // original ordering so the user sees 104, 103, 102… same as TV.
  const ordered = trades
    .map((t, i) => ({ t, number: i + 1 }))
    .reverse();

  const fmtTs = (ts: string | null): string => {
    if (!ts) return "—";
    const d = new Date(ts);
    const date = d.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
    const time = d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: false });
    return `${date}, ${time}`;
  };

  return (
    <div className="st-pane">
      <div className="table-scroll" style={{ maxHeight: 480, overflowY: "auto" }}>
        <table className="grid tv-trades">
          <thead>
            <tr>
              <th>Trade number ↓</th>
              <th>Type</th>
              <th>Date and time</th>
              <th className="num">Price</th>
              <th className="num">Size</th>
              <th className="num">Net PnL</th>
              <th className="num">Return</th>
            </tr>
          </thead>
          <tbody>
            {ordered.map(({ t, number }) => {
              const dirClass = t.direction === "long" ? "up" : "down";
              const pnlClass = t.pnl >= 0 ? "up" : "down";
              const shares = t.qty > 0 ? Math.round(t.qty) : null;
              const sizeUsd = shares !== null ? shares * t.entry_price : 0;
              return (
                <>
                  <tr
                    key={`${number}-exit`}
                    className="trade-row tv-trade-exit"
                    onClick={() => click(t, number - 1, "exit")}
                    title="Click to show this trade on the chart"
                  >
                    <td className="num faint" rowSpan={2}>
                      <div className="tv-trade-num">
                        {number}
                        <span className={`tv-trade-side ${dirClass}`}>{t.direction === "long" ? "Long" : "Short"}</span>
                      </div>
                    </td>
                    <td>Exit</td>
                    <td className="small nowrap">{fmtTs(t.exit_ts)}</td>
                    <td className="num">{t.exit_price === null ? "—" : `${formatNum(t.exit_price, 2)} USD`}</td>
                    <td className="num" rowSpan={2}>
                      {shares !== null ? (
                        <>
                          {shares}
                          <div className="small faint">{formatNum(sizeUsd / 1000, 2)}K USD</div>
                        </>
                      ) : "—"}
                    </td>
                    <td className={`num ${pnlClass}`} rowSpan={2}>
                      {t.pnl >= 0 ? "+" : ""}{formatNum(t.pnl, 2)}
                      <span className="unit"> USD</span>
                    </td>
                    <td className={`num ${pnlClass}`} rowSpan={2}>
                      {t.pnl_pct >= 0 ? "+" : ""}{formatPct(t.pnl_pct, 2)}
                    </td>
                  </tr>
                  <tr
                    key={`${number}-entry`}
                    className="trade-row tv-trade-entry"
                    onClick={() => click(t, number - 1, "entry")}
                  >
                    <td>Entry</td>
                    <td className="small nowrap">{fmtTs(t.entry_ts)}</td>
                    <td className="num">{formatNum(t.entry_price, 2)} USD</td>
                  </tr>
                </>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** A single on/off row with a hint tooltip, persisted. Used for the Style
 *  and Visibility tabs of the Strategy Settings dialog. */
function StyleToggleRow({ label, storageKey, defaultValue, hint }: {
  label: string;
  storageKey: string;
  defaultValue: boolean;
  hint?: string;
}) {
  const [on, setOn] = usePersisted(storageKey, defaultValue);
  return (
    <div className="st-settings-row" title={hint}>
      <label>{label}</label>
      <input type="checkbox" checked={on} onChange={(e) => setOn(e.target.checked)} />
    </div>
  );
}

/** TradingView-style pill dropdown: an icon + label button that opens a
 *  popover anchored below it. Children receive a `close` callback so an
 *  item click can dismiss the popover. */
/** Icon atoms matching TradingView's thin-stroke SVG style, replacing the
 *  emoji we had before -- emoji render at platform font sizes and look
 *  nothing like TV's crisp 16px strokes. */
function TvDdIcon({ name }: { name: "calendar" | "dollar" | "scope" }) {
  const common = { width: 14, height: 14, viewBox: "0 0 24 24", fill: "none",
    stroke: "currentColor", strokeWidth: 1.8, strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const };
  if (name === "calendar") return (
    <svg {...common}>
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  );
  if (name === "dollar") return (
    <svg {...common}>
      <circle cx="12" cy="12" r="10" />
      <path d="M15 9a3 3 0 0 0-3-2h-1a2.5 2.5 0 0 0 0 5h2a2.5 2.5 0 0 1 0 5h-1a3 3 0 0 1-3-2" />
      <path d="M12 7v10" />
    </svg>
  );
  return ( /* scope / magnifier for detalization */
    <svg {...common}>
      <circle cx="11" cy="11" r="7" />
      <line x1="16" y1="16" x2="21" y2="21" />
    </svg>
  );
}

function TvDropdown({ icon, label, title, children }: {
  icon: "calendar" | "dollar" | "scope";
  label: string;
  title?: string;
  children: (close: () => void) => React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("mousedown", onDown);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("mousedown", onDown);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div className="tvdd-wrap" ref={ref}>
      <motion.button
        className={`tvdd-trigger ${open ? "tvdd-trigger-on" : ""}`}
        onClick={() => setOpen((v) => !v)}
        title={title}
        whileHover={{ y: -1 }}
        whileTap={{ scale: 0.97 }}
        transition={{ type: "spring", stiffness: 500, damping: 30 }}
      >
        <span className="tvdd-icon"><TvDdIcon name={icon} /></span>
        <span className="tvdd-label">{label}</span>
        <motion.span
          className="tvdd-caret"
          animate={{ rotate: open ? 180 : 0 }}
          transition={{ type: "spring", stiffness: 500, damping: 30 }}
        >▾</motion.span>
      </motion.button>
      <AnimatePresence>
        {open && (
          <motion.div
            className="tvdd-pop"
            initial={{ opacity: 0, y: -6, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -4, scale: 0.98 }}
            transition={{ type: "spring", stiffness: 420, damping: 32, mass: 0.6 }}
          >
            {children(() => setOpen(false))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
